<template>
  <InfoNotice
    v-if="isCreatingNewProfile && !deliveryUnavailable"
    :text="$t('new_address_auto_save_info')"
  />
  <InfoNotice
    v-else-if="isUpdatingProfile && !deliveryUnavailable"
    :text="$t('edit_address_auto_save_info')"
  />
  <div
    v-if="shouldShowProfileButtons"
    class="flex items-center flex-col md:flex-row"
  >
    <!-- «Все адреса» – кнопка-триггер -->
    <UiButton
      color="secondary"
      size="large"
      class="step-btn mr-2"
      @click="openProfiles"
      data-qa="step0-all-addresses-btn"
    >
      <template #icon><LeadingIcon /></template>
      {{ $t('all_addresses') }}
    </UiButton>
    <UiButton
      color="outline"
      size="large"
      class="step-btn profiles__new-address"
      :class="{ 'skeleton-pulse': isProcessing }"
      @click="$emit('createNewAddress')"
      data-qa="step0-new-address-btn"
    >
      <template #icon><LeadingSecondIcon /></template>
      {{ $t('new_delivery_address') }}
    </UiButton>
  </div>
  <!-- Модальное окно со списком профилей -->
  <ProfilesList
    v-if="showModal"
    :selectedProfile="userInfoId"
    :listLoading="listLoading"
    :isProcessing="isProcessing"
    @close="closeProfiles"
    @selectActiveProfile="chooseProfile"
    @wasLastProfile="hideBtns"
    @createNewAddress="$emit('createNewAddress')"
  />
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import ProfilesList from './Profiles.vue'
import LeadingIcon from '@/components/svg/leadingIcon.vue'
import LeadingSecondIcon from '@/components/svg/leadingSecondIcon.vue'
import InfoNotice from '@/components/InfoNotice.vue'
import UiButton from '@/components/ui/UiButton.vue'
import { trackProfileAction } from '@/utils/checkoutAnalytics'

/* ------- props / emits ------- */
const props = defineProps({
  modelValue: { type: Number, default: null },
  isProcessing: { type: Boolean, default: false },
  deliveryUnavailable: { type: Boolean, default: false },
})
const emit = defineEmits(['update:modelValue', 'createNewAddress'])

const store = useStore()

const isCreatingNewProfile = computed(() => store.getters.isCreatingNewProfile)
const isUpdatingProfile = computed(() => store.getters.isUpdatingProfile)

const lastProfileId = computed(() => store.state.SELECTED_PROFILE?.PROFILE_ID)
const userInfoId = computed({
  get: () => (props.modelValue ? props.modelValue : lastProfileId.value),
  set: (v) => emit('update:modelValue', v),
})

/* Если в сторе ещё нет профилей – будем загружать */
const hasProfiles = computed(() => store.state.deliveryProfiles?.length > 0)
// Не должна быть реактивной: нам нужно состояние только при загрузке чекаута - есть ли профили
const hadProfileOnInit = !!(
  store.state.SELECTED_PROFILE && store.state.SELECTED_PROFILE.PROFILE_ID
)

const showModal = ref(false)
const listLoading = ref(false)
const loadError = ref(null)

// const hadProfile = computed(() => hadProfileOnInit)
const hadProfile = ref(hadProfileOnInit)
// При работе с профилями может не оказаться ни одного профиля, хотя изначально были - скрываем кнопки!
function hideBtns() {
  hadProfile.value = false
}
const shouldShowProfileButtons = computed(() => {
  return hadProfile.value
})

async function openProfiles() {
  trackProfileAction({
    action: 'profile_button_click',
    currentStep: 'delivery',
  })
  showModal.value = true
  store.commit('setFioFieldFocusState', false)
  if (!hasProfiles.value) {
    listLoading.value = true
    loadError.value = null
    try {
      await store.dispatch('GET_DELIVERY_PROFILES')
      store.dispatch('GET_PROFILE_PRICES')
    } catch (e) {
      loadError.value = e?.message || 'Load error'
      console.error(e)
    } finally {
      listLoading.value = false
    }
  }
}

function closeProfiles() {
  showModal.value = false
}

/** Выбрали профиль в списке – сообщаем наверх */
function chooseProfile(id) {
  trackProfileAction({
    action: 'profile_changed',
    currentStep: 'delivery',
  })
  emit('update:modelValue', id) // v-model
  closeProfiles()
}
</script>

<style scoped lang="scss">
.step-btn {
  margin-bottom: var(--c2-ot-dimension-100);
  width: max-content;

  @media screen and (max-width: 1023px) {
    width: 100%;
  }
}
</style>
